import { extractImageFrames, clampTo } from '../../tools';
import type { baiduModelConfig } from '../types';

export const minimaxConfigs: Record<string, baiduModelConfig> = {
    'hailuo-h3': {
        // https://tcnxnzs113h4.feishu.cn/wiki/OysuwSp6PiJj52kIS0VcBWr8nbf
        // https://platform.minimaxi.com/docs/api-reference/video-generation-v2-create
        apiPath: '/v3/aigc/minimax/v2/video_generation',
        pollApiPath: '/v3/aigc/minimax/v2/query/video_generation/{id}',
        enableTranslation: false,
        statusMapping: {
            taskIdPath: 'task_id',
            statePath: 'task.status',
            success: ['succeeded'],
            failed: ['failed', 'cancelled', 'expired'],
            processing: ['queued', 'running'],
            videoUrlPath: 'task.content.url',
            errorPath: 'task.error.message',
        },
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUris, audioUris, duration, aspectRatio, quality, modelUi } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 9);
            const videos = (videoUris ?? []).filter(Boolean).slice(0, 3);
            const audios = (audioUris ?? []).filter(Boolean).slice(0, 3);
            if (frameMode === 'first') {
                return {
                    model: 'MiniMax-H3',
                    content: [
                        {
                            type: 'text',
                            text: prompt.slice(0, 7000),
                        },
                        {
                            type: 'image_url',
                            image_url: { url: firstImage! },
                            role: 'first_frame',
                        },
                    ],
                    ratio: 'adaptive',
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo((quality || '2K').toUpperCase(), ['768P', '2K'] as const, '2K'),
                };
            } else if (frameMode === 'first_last') {
                return {
                    model: 'MiniMax-H3',
                    content: [
                        {
                            type: 'text',
                            text: prompt.slice(0, 7000),
                        },
                        {
                            type: 'image_url',
                            image_url: { url: firstImage! },
                            role: 'first_frame',
                        },
                        {
                            type: 'image_url',
                            image_url: { url: lastImage || firstImage! },
                            role: 'last_frame',
                        },
                    ],
                    ratio: 'adaptive',
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo((quality || '2K').toUpperCase(), ['768P', '2K'] as const, '2K'),
                };
            } else if (frameMode === 'ref') {
                return {
                    model: 'MiniMax-H3',
                    content: [
                        {
                            type: 'text',
                            text: prompt.slice(0, 7000),
                        },
                        ...images.map(url => ({
                            type: 'image_url' as const,
                            image_url: { url },
                            role: 'reference_image' as const,
                        })),
                        ...videos.map(url => ({
                            type: 'video_url' as const,
                            video_url: { url },
                            role: 'reference_video' as const,
                        })),
                        ...audios.map(url => ({
                            type: 'audio_url' as const,
                            audio_url: { url },
                            role: 'reference_audio' as const,
                        })),
                    ],
                    ratio: clampTo(aspectRatio || 'adaptive', ['adaptive', '21:9', '16:9', '4:3', '1:1', '3:4', '9:16'] as const, 'adaptive'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo((quality || '2K').toUpperCase(), ['768P', '2K'] as const, '2K'),
                };
            } else {
                return {
                    model: 'MiniMax-H3',
                    content: [
                        {
                            type: 'text',
                            text: prompt.slice(0, 7000),
                        },
                    ],
                    ratio: clampTo(aspectRatio || '16:9', ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo((quality || '2K').toUpperCase(), ['768P', '2K'] as const, '2K'),
                };
            }
        },
    },
};